import moment from "moment";
import { isPhoneComplete } from "./phoneUtils";
import {
  isIdExpiryRequired,
  isMainIdValidationActive,
  validateClientIdFormat,
  validateOtherIds,
} from "./clientIdentity";
import { composeClientName, isNonCorporateClientType, isPrimarilyCjkName } from "./clientNameUtils";

const isIndividual = (formValues) => formValues?.type === "INDIVIDUAL";
const isNonCorporate = (formValues) =>
  isNonCorporateClientType(formValues?.type);

export const CLIENT_CONFIRM_FIELD_LABELS = {
  idType: "ID Type",
  id: "ID",
  idIssueCountry: "ID Issue Country",
  idDocExpiryDate: "Identity Doc Expiry Date",
  otherIds: "Other IDs",
  salutation: "Salutation",
  surname: "Surname",
  givenName: "Given Name",
  name: "English Name",
  cname: "Chinese Name",
  phone: "Phone Number",
  nationality: "Nationality",
  residentialCountry: "Residential Country",
  language: "Language",
  gender: "Gender",
  maritalStatus: "Marital Status",
  occupation: "Occupation",
  educationLevel: "Education Level",
  placeOfBirth: "Country of Birth",
  accountCloseDate: "Account Close Date",
  consultants: "Consultant",
  birthday: "Date of Birth",
};

const requiredMessage = (label, t) =>
  t ? t(`${label} is required`) : `${label} is required`;

const isRequiredValueMissing = (value) => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value instanceof Date) return !moment(value).isValid();
  return !String(value).trim();
};

const requiredWhenIndividual =
  (label, t) =>
  (value, formValues) => {
    if (!isIndividual(formValues)) return null;
    if (isRequiredValueMissing(value)) return requiredMessage(label, t);
    return null;
  };

const requiredWhenNonCorporate =
  (label, t) =>
  (value, formValues) => {
    if (!isNonCorporate(formValues)) return null;
    if (isRequiredValueMissing(value)) return requiredMessage(label, t);
    return null;
  };

const requiredWhenNonHkid =
  (label, t) =>
  (value, formValues) => {
    if (!isIdExpiryRequired(formValues?.idType)) return null;
    if (isRequiredValueMissing(value)) return requiredMessage(label, t);
    return null;
  };

const requiredWhenMainId =
  (label, t) =>
  (value, formValues) => {
    if (!isMainIdValidationActive(formValues)) return null;
    if (isRequiredValueMissing(value)) return requiredMessage(label, t);
    return null;
  };

const validateComposedNameLength = (surname, givenName, t) => {
  const composed = composeClientName(surname, givenName);
  if (isPrimarilyCjkName(composed)) {
    if (composed.length < 2) {
      return t("Name must have at least 2 characters");
    }
    return null;
  }
  if (composed.length < 4) {
    return t("Name must have at least 4 letters");
  }
  return null;
};

export const buildClientNameValidate = (t) => {
  const requiredNonCorporate = (label) => requiredWhenNonCorporate(label, t);

  return {
    surname: requiredNonCorporate("Surname"),
    givenName: (value, formValues) => {
      const requiredError = requiredNonCorporate("Given Name")(
        value,
        formValues
      );
      if (requiredError) return requiredError;
      if (!isNonCorporate(formValues)) return null;
      return validateComposedNameLength(formValues.surname, value, t);
    },
    name: (value, formValues) => {
      if (isNonCorporate(formValues)) return null;
      if (isRequiredValueMissing(value)) {
        return requiredMessage("English Name", t);
      }
      if (value?.length < 4) {
        return t("Name must have at least 4 letters");
      }
      return null;
    },
  };
};

export const buildIndividualClientValidate = (t) => {
  const required = (label) => requiredWhenIndividual(label, t);
  const requiredMainId = (label) => requiredWhenMainId(label, t);

  return {
    idType: requiredMainId("ID Type"),
    id: (value, formValues) => {
      if (!isMainIdValidationActive(formValues)) return null;

      const requiredError = requiredMainId("ID")(value, formValues);
      if (requiredError) return requiredError;

      return validateClientIdFormat(value, formValues.idType, t);
    },
    idIssueCountry: requiredMainId("ID Issue Country"),
    idDocExpiryDate: requiredWhenNonHkid("Identity Doc Expiry Date", t),
    salutation: required("Salutation"),
    ...buildClientNameValidate(t),
    nationality: required("Nationality"),
    residentialCountry: required("Residential Country"),
    language: required("Language"),
    gender: required("Gender"),
    maritalStatus: required("Marital Status"),
    placeOfBirth: required("Country of Birth"),
    consultants: required("Consultant"),
    otherIds: (value, formValues) => validateOtherIds(value, formValues, t),
    birthday: (value, formValues) => {
      if (!isIndividual(formValues)) return null;
      if (!value) return t("Date of Birth is required");
      const date = moment(value);
      if (!date.isValid()) return t("Date of Birth is required");
      return null;
    },
  };
};

export const buildClientUpdateValidate = (t) => {
  return {
    ...buildIndividualClientValidate(t),
    birthday: (value, formValues) => {
      if (!isIndividual(formValues)) return null;
      if (!value) return null;
      const date = moment(value);
      if (!date.isValid()) return t("Date of Birth is invalid");
      return null;
    },
    phone: (value, formValues) => {
      if (!isIndividual(formValues)) return null;
      if (!isPhoneComplete(value)) return null;
      return null;
    },
  };
};
